import { useState } from "react";
import { saveSettings, type AiRollout, type AiStatus, type GetIdToken, type StatsSettings } from "./api";
import { formatDateTime } from "./aggregate";

const ROLLOUT_LABELS: Record<AiRollout, string> = {
  all: "Für alle (Standard)",
  admins: "Nur Admins (Test)",
  off: "Aus",
};

const PROVIDER_STATUS: Record<string, string> = {
  no_credit: "Guthaben aufgebraucht",
  key_invalid: "Key ungültig oder abgelaufen",
  no_workspace: "Workspace fehlt",
  no_key: "kein Key",
  rate_limited: "kurz ausgelastet",
  overloaded: "überlastet",
  failed: "Fehler",
};

// Fehler, die ohne Eingreifen nicht von selbst verschwinden.
const BLOCKING = new Set(["no_key", "key_invalid", "no_workspace", "no_credit"]);

export interface AiWarning {
  level: "red" | "amber";
  text: string;
}

export function daysUntil(isoDate: string, now = new Date()): number {
  const [y, m, d] = isoDate.split("-").map(Number);
  const end = new Date(y, m - 1, d);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((end.getTime() - today.getTime()) / 86_400_000);
}

const formatDate = (isoDate: string) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" });
};

// Hinweise, die oben im Dashboard auffallen sollen.
export function aiWarnings(s: AiStatus): AiWarning[] {
  const out: AiWarning[] = [];
  if (s.rollout === "off") return out;
  if (!s.hasKey) out.push({ level: "red", text: "KI-Raumerkennung: Auf dem Server ist kein API-Key hinterlegt." });
  const days = daysUntil(s.keyExpires);
  if (days < 0) out.push({ level: "red", text: `Der KI-API-Key ist am ${formatDate(s.keyExpires)} abgelaufen. Bitte einen neuen Key anlegen.` });
  else if (days <= 30)
    out.push({ level: "amber", text: `Der KI-API-Key läuft in ${days} ${days === 1 ? "Tag" : "Tagen"} ab (${formatDate(s.keyExpires)}). Rechtzeitig einen neuen Key anlegen.` });
  const down = (s.providers ?? []).filter((p) => p.status !== "ok");
  if (down.length > 0 && down.length < (s.providers ?? []).length)
    out.push({
      level: "amber",
      text: `KI-Rückfall aktiv: ${down.map((p) => `${p.provider} (${PROVIDER_STATUS[p.status] ?? p.status})`).join(", ")}. Die Erkennung läuft über den anderen Anbieter weiter.`,
    });
  const e = s.lastError;
  if (e && BLOCKING.has(e.code) && (!s.lastOk || e.ts > s.lastOk))
    out.push({ level: "red", text: `KI-Raumerkennung funktioniert nicht: ${e.message} (seit ${formatDateTime(e.ts)})` });
  return out;
}

export function AiWarnings({ status }: { status: AiStatus | null }) {
  if (!status) return null;
  const list = aiWarnings(status);
  if (list.length === 0) return null;
  return (
    <div className="space-y-2">
      {list.map((w, i) => (
        <div
          key={i}
          role="alert"
          className={`rounded-md border px-4 py-3 text-sm ${
            w.level === "red" ? "border-red-300 bg-red-50 text-red-900" : "border-amber-300 bg-amber-50 text-amber-900"
          }`}
        >
          {w.level === "red" ? "⚠️ " : "⏳ "}
          {w.text}
        </div>
      ))}
    </div>
  );
}

export function AiCard({
  status,
  getIdToken,
  onChanged,
}: {
  status: AiStatus;
  getIdToken: GetIdToken;
  onChanged: (s: StatsSettings) => void;
}) {
  const [expires, setExpires] = useState(status.keyExpires);
  const [msg, setMsg] = useState<string | null>(null);
  const days = daysUntil(status.keyExpires);

  const save = async (patch: Parameters<typeof saveSettings>[1]) => {
    setMsg(null);
    try {
      onChanged(await saveSettings(getIdToken, patch));
      setMsg("✓ Gespeichert");
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-1">KI-Raumerkennung</h2>
      <p className="text-sm text-kampmeyer-neutral-500 mb-4">
        Die KI schlägt für jedes Bild den Raumnamen vor (Küche, Bad, Zimmer …). Zuerst wird Google Gemini gefragt; fällt
        Gemini aus, springt Anthropic Claude automatisch ein. Die Bilder gehen dafür verkleinert an den jeweiligen Anbieter;
        die API-Keys liegen nur auf dem Server.
      </p>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm mb-5">
        <Item label="API-Key">
          {status.hasKey ? <span className="text-green-700">hinterlegt</span> : <span className="text-red-700">fehlt</span>}
          {status.model && <span className="text-kampmeyer-neutral-500"> · {status.model}</span>}
        </Item>
        {status.providers && status.providers.length > 0 && (
          <Item label="Anbieter (Reihenfolge)">
            {status.providers.map((p, i) => (
              <div key={p.provider}>
                {i + 1}. {p.provider}:{" "}
                {p.status === "ok" ? (
                  <span className="text-green-700">bereit</span>
                ) : (
                  <span className="text-red-700">{PROVIDER_STATUS[p.status] ?? p.status}</span>
                )}
              </div>
            ))}
          </Item>
        )}
        <Item label="Gültig bis">
          {formatDate(status.keyExpires)}{" "}
          <span className={days < 0 ? "text-red-700" : days <= 30 ? "text-amber-700" : "text-kampmeyer-neutral-500"}>
            ({days < 0 ? "abgelaufen" : `noch ${days} Tage`})
          </span>
        </Item>
        <Item label="Erkannte Bilder diesen Monat">{status.imagesThisMonth.toLocaleString("de-DE")}</Item>
        <Item label="Zuletzt erfolgreich">{status.lastOk ? formatDateTime(status.lastOk) : "–"}</Item>
        <Item label="Letzter Fehler">
          {status.lastError ? (
            <>
              {status.lastError.message}{" "}
              <span className="text-kampmeyer-neutral-500">({formatDateTime(status.lastError.ts)})</span>
            </>
          ) : (
            "–"
          )}
        </Item>
      </dl>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="text-sm font-medium">Freigabe</span>
        <div className="inline-flex rounded-md border border-kampmeyer-neutral-300 overflow-hidden text-sm" role="group">
          {(Object.keys(ROLLOUT_LABELS) as AiRollout[]).map((r) => (
            <button
              key={r}
              onClick={() => r !== status.rollout && void save({ aiRollout: r })}
              aria-pressed={status.rollout === r}
              className={`px-3 py-1.5 transition ${
                status.rollout === r ? "bg-kampmeyer-primary text-white" : "hover:bg-kampmeyer-neutral-100"
              }`}
            >
              {ROLLOUT_LABELS[r]}
            </button>
          ))}
        </div>
      </div>

      <form
        className="flex flex-wrap items-center gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (expires && expires !== status.keyExpires) void save({ aiKeyExpires: expires });
        }}
      >
        <label className="text-sm font-medium" htmlFor="ai-key-expires">
          Ablaufdatum des Keys
        </label>
        <input
          id="ai-key-expires"
          type="date"
          value={expires}
          onChange={(e) => setExpires(e.target.value)}
          className="px-3 py-2 text-sm rounded border border-kampmeyer-neutral-300 bg-white text-kampmeyer-neutral-900"
        />
        <button type="submit" className="btn-secondary !px-4 !py-2 text-sm" disabled={!expires || expires === status.keyExpires}>
          Speichern
        </button>
        {msg && <span className={`text-sm ${msg.startsWith("✓") ? "text-green-700" : "text-red-700"}`}>{msg}</span>}
      </form>
    </div>
  );
}

function Item({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-kampmeyer-neutral-500">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}
